<template>
  <div class="calendar-tutorial" v-if="show">
    <div class="tutorial-overlay"></div>
    
    <!-- Step 1: Create Event -->
    <div v-if="currentStep === 1" class="tutorial-step" :style="stepOnePosition">
      <div class="tutorial-pointer"></div>
      <div class="tutorial-content">
        <h3 style="color: var(--v-mainGreen-base) !important;">{{ $t('createEventTitle') }}</h3>
        <p>{{ $t('createEventDesc') }}</p>
      </div>
    </div>

    <!-- Step 2: View Event Details -->
    <div v-if="currentStep === 2" class="tutorial-step" :style="stepTwoPosition">
      <div class="tutorial-pointer"></div>
      <div class="tutorial-content">
        <h3 style="color: var(--v-mainGreen-base) !important;">{{ $t('viewEventTitle') }}</h3>
        <p>{{ $t('viewEventDesc') }}</p>
      </div>
    </div>

    <!-- Step 3: Weekly View -->
    <div v-if="currentStep === 3" class="tutorial-step" :style="stepThreePosition">
      <div class="tutorial-pointer"></div>
      <div class="tutorial-content">
        <h3 style="color: var(--v-mainGreen-base) !important;">{{ $t('weeklyViewTitle') }}</h3>
        <p>{{ $t('weeklyViewDesc') }}</p>
      </div>
    </div>

    <!-- Navigation -->
    <div class="tutorial-nav">
      <v-btn style="background-color: transparent;" text small @click="prevStep" v-if="currentStep > 1">
        <v-icon small left>fa-chevron-left</v-icon>
        {{ $t('prev') }}
      </v-btn>
      <v-btn style="background-color: transparent;" text small @click="nextStep" v-if="currentStep < 3">
        {{ $t('next') }}
        <v-icon small right>fa-chevron-right</v-icon>
      </v-btn>
      <v-btn style="background-color: transparent;" text small @click="finish" v-if="currentStep === 3">
        {{ $t('finish') }}
        <v-icon small right>fa-check</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalendarTutorial',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 1,
      stepOnePosition: {
        top: '50%',
        left: '50%'
      },
      stepTwoPosition: {
        top: '30%',
        left: '40%'
      },
      stepThreePosition: {
        top: '20%',
        left: '30%'
      }
    }
  },
  methods: {
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    finish() {
      this.$emit('close');
      localStorage.setItem('calendar-tutorial-completed', 'true');
    },
    updatePositions() {
      const calendar = document.querySelector('.v-calendar');
      if (!calendar) return;

      const rect = calendar.getBoundingClientRect();
      
      // Position for day box click (create event)
      this.stepOnePosition = {
        top: `${rect.top + rect.height / 2}px`,
        left: `${rect.left + rect.width / 2}px`
      };

      // Position for event click (view details)
      this.stepTwoPosition = {
        top: `${rect.top + rect.height / 3}px`,
        left: `${rect.left + rect.width / 3}px`
      };

      // Position for day number click (weekly view)
      // Position to the left of the day number on the right side
      this.stepThreePosition = {
        top: `${rect.top + 50}px`,
        left: `${rect.left + rect.width - 300}px`, // Move tooltip to the left of the day number
        transform: 'none' // Override default transform
      };
    }
  },
  mounted() {
    // Wait for any scrolling to finish before calculating positions
    setTimeout(() => {
      this.updatePositions();
      window.addEventListener('resize', this.updatePositions);
    }, 600);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePositions);
  }
}
</script>



<style lang="scss" scoped>
.calendar-tutorial {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;

  .tutorial-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }

  .tutorial-step {
    position: absolute;
    transform: translate(-50%, -50%);
    animation: bounce 1s infinite;
    z-index: 1001;

    .tutorial-pointer {
      width: 20px;
      height: 20px;
      background: var(--v-mainGreen-base);
      border-radius: 50%;
      margin: 0 auto;
      box-shadow: 0 0 0 5px rgba(0, 195, 97, 0.3);
    }

    .tutorial-content {
      background: var(--v-mainWhite-base);
      padding: 15px;
      border-radius: 8px;
      margin-top: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      min-width: 250px;
      
      h3 {
        color: var(--v-mainGreen-base);
        margin-bottom: 8px;
        font-family: 'Avenir', sans-serif;
      }

      p {
        color: var(--v-mainBlack-base);
        margin: 0;
        font-family: 'Avenir', sans-serif;
      }
    }

    &:nth-child(4) { // Weekly view step
      transform: none !important; // Override transform for weekly view step
      
      .tutorial-pointer {
        float: right;
        margin-right: 20px;
      }
      
      .tutorial-content {
        clear: both;
        margin-top: 20px;
      }
    }
  }

  .tutorial-nav {
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 16px;
    background: var(--v-mainWhite-base);
    padding: 8px 16px;
    border-radius: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
  }
}
</style>
